import { useLang } from '../i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// src/components/UI.js — Premium SaaS Components
// Inspired by 21st.dev design patterns

import Icon from './Icons';

/* ── CardCN — Premium with gradient bg, border glow on hover ────────────── */
export function Card({ children, style = {}, className = '', onClick, glow, accent }) {
  const cls = [onClick ? 'card-premium row-card' : '', className].filter(Boolean).join(' ');
  const handleKeyDown = onClick ? (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
  } : undefined;
  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{
        background: 'linear-gradient(180deg, var(--bg-card) 0%, rgba(6,15,28,0.95) 100%)',
        border: '1px solid var(--border-card)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        ...(glow ? { boxShadow: `0 0 20px ${glow}12, inset 0 1px 0 rgba(255,255,255,0.03)` } : {}),
        ...style,
      }}
      className={cls}
    >
      {accent && <div style={{
        position:'absolute', top:0, left:0, right:0, height:2,
        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
        opacity: 0.5, pointerEvents:'none',
      }} />}
      {children}
    </div>
  );
}

/* ── GlassCard — Frosted glass effect card ──────────────────────────────── */
export function GlassCard({ children, style = {}, className = '' }) {
  return (
    <div className={className} style={{
      background: 'linear-gradient(135deg, rgba(8,22,40,0.8) 0%, rgba(6,15,28,0.9) 100%)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(26,48,80,0.6)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-elevated), inset 0 1px 0 rgba(255,255,255,0.03)',
      position: 'relative', overflow: 'hidden',
      ...style,
    }}>
      {/* Subtle top highlight */}
      <div style={{
        position: 'absolute', top: 0, left: '10%', right: '10%', height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(0,207,200,0.2), transparent)',
        pointerEvents: 'none',
      }} />
      {children}
    </div>
  );
}

/* ── BadgeCN — With dot indicator ───────────────────────────────────────── */
export function Badge({ text, color = 'var(--cyan)', size = 11, dot }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: color + '18', color, border: `1px solid ${color}30`,
      borderRadius: 100, padding: `2px ${size < 10 ? 6 : 10}px`,
      fontSize: size, fontWeight: 700, letterSpacing: '0.04em',
      fontFamily: 'var(--font-display)', lineHeight: 1.4,
      whiteSpace: 'nowrap',
    }}>
      {dot && <span style={{
        width: 6, height: 6, borderRadius: '50%', background: color,
        boxShadow: `0 0 6px ${color}`,
      }} />}
      {text}
    </span>
  );
}

/* ── Estado badge — with animated dot ───────────────────────────────────── */
const ESTADO_COLORS = {
  solicitada:    '#7a9fc0',
  aceptada:      '#00CFC8',
  documentacion: '#FFB300',
  visita:        '#0A6EFA',
  evaluacion:    '#8B5CF6',
  votacion:      '#F97316',
  decision:      '#00C896',
  adjudicada:    '#00C896',
  rechazada:     '#E53935',
  notificada:          '#00CFC8',
  notificada_rechazo:  '#E53935',
  gala_confirmada:     '#FFB300',
  premio_otorgado:     '#00C896',
  titularidad_activa:  '#9C27B0',
};
function getEstadoLabel(estado, t) {
  const labels = {
    solicitada:    t('estado_solicitada')    || 'Solicitada',
    aceptada:      t('estado_aceptada')      || 'Aceptada',
    documentacion: t('estado_documentacion') || 'Documentacion',
    visita:        t('estado_visita')        || 'Visita programada',
    evaluacion:    t('estado_evaluacion')    || 'En evaluacion',
    votacion:      t('estado_votacion')      || 'En votacion',
    decision:      t('estado_decision')      || 'Decision pendiente',
    adjudicada:    t('estado_adjudicada')    || 'Adjudicada',
    rechazada:     t('estado_rechazada')     || 'No adjudicada',
    notificada:          t('estado_notificada')          || 'Notificada',
    notificada_rechazo:  t('estado_notificada_rechazo')  || 'Rechazo notificado',
    gala_confirmada:     t('estado_gala_confirmada')     || 'Gala confirmada',
    premio_otorgado:     t('estado_premio_otorgado')     || 'Premio otorgado',
    titularidad_activa:  t('estado_titularidad_activa')  || 'Titularidad activa',
  };
  return labels[estado] || estado;
}
export function EstadoBadge({ estado }) {
  const { t } = useLang();
  const color = ESTADO_COLORS[estado] || '#7a9fc0';
  const isActive = !['rechazada', 'notificada_rechazo', 'titularidad_activa'].includes(estado);
  return <Badge text={getEstadoLabel(estado, t)} color={color} dot={isActive} />;
}

/* ── Botones — Premium with subtle gradient ─────────────────────────────── */
export function Btn({ children, onClick, variant = 'primary', size = 'md',
                       disabled = false, loading = false, icon, style = {} }) {
  const sizes = { sm: '7px 14px', md: '10px 20px', lg: '13px 28px' };
  const fontSizes = { sm: 12, md: 13, lg: 15 };
  const variants = {
    primary:  { background: 'linear-gradient(135deg, #00CFC8 0%, #00b0a8 100%)', color: 'var(--bg-base)',
                boxShadow: '0 2px 8px rgba(0,207,200,0.25), inset 0 1px 0 rgba(255,255,255,0.2)' },
    amber:    { background: 'linear-gradient(135deg, #FFB300 0%, #e6a000 100%)', color: 'var(--bg-base)',
                boxShadow: '0 2px 8px rgba(255,179,0,0.25)' },
    danger:   { background: 'linear-gradient(135deg, #E53935 0%, #c62828 100%)', color: '#fff',
                boxShadow: '0 2px 8px rgba(229,57,53,0.25)' },
    ghost:    { background: 'transparent', color: 'var(--cyan)', border: '1px solid rgba(0,207,200,0.2)' },
    outline:  { background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)' },
    success:  { background: 'linear-gradient(135deg, #00C896 0%, #00a87e 100%)', color: 'var(--bg-base)',
                boxShadow: '0 2px 8px rgba(0,200,150,0.25)' },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: sizes[size], borderRadius: 'var(--radius-sm)',
        fontSize: fontSizes[size], fontWeight: 700,
        fontFamily: 'var(--font-display)', letterSpacing: '0.02em',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        border: 'none', transition: 'all 150ms ease',
        ...v, ...style,
      }}
    >
      {loading ? <Spinner size={14} color={v.color} /> : icon}
      {children}
    </button>
  );
}

/* ── Spinner — Smoother ─────────────────────────────────────────────────── */
export function Spinner({ size = 20, color = 'var(--cyan)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         style={{ animation: 'spin 0.8s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2.5" opacity="0.2"  />
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2.5"
              strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" />
    </svg>
  );
}

/* ── EmptyState — with subtle animation ─────────────────────────────────── */
export function EmptyState({ icon = 'layers', title, subtitle, action }) {
  return (
    <div className="section-fade" style={{ display:'flex', flexDirection:'column', alignItems:'center',
                  justifyContent:'center', padding:'64px 24px', gap:16, textAlign:'center' }}>
      <div style={{ width:64, height:64, borderRadius:'50%',
                    background:'linear-gradient(135deg, var(--bg-elevated) 0%, var(--bg-card) 100%)',
                    border:'1px solid var(--border)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
        <Icon name={icon} size={28} color="var(--text-muted)" />
      </div>
      <div>
        <div style={{ fontSize:15, fontWeight:600, color:'var(--text)', marginBottom:4 }}>{title}</div>
        {subtitle && <div style={{ fontSize:13, color:'var(--text-secondary)', maxWidth:280 }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

/* ── SectionHeader — with accent line ───────────────────────────────────── */
export function SectionHeader({ title, action, color }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        {color && <div style={{ width:3, height:14, borderRadius:2, background:color }} />}
        <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.1em',
                       color:'var(--text-secondary)', textTransform:'uppercase',
                       fontFamily:'var(--font-display)' }}>
          {title}
        </span>
      </div>
      {action}
    </div>
  );
}

/* ── Input — with floating label + icon + focus ring ────────────────────── */
let _inputIdCounter = 0;
export function Input({ label, icon, type = 'text', error, floating, id: propId, ...props }) {
  const autoId = useRef(propId || `input-${++_inputIdCounter}`).current;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {label && !floating && (
        <label htmlFor={autoId} style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)',
                        letterSpacing:'0.08em', textTransform:'uppercase',
                        fontFamily:'var(--font-display)' }}>
          {label}
        </label>
      )}
      <div style={{ position:'relative' }} className={floating ? 'floating-input' : undefined}>
        {icon && (
          <div style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)',
                        color:'var(--text-muted)', pointerEvents:'none',
                        transition:'color 200ms ease', zIndex:1 }}>
            <Icon name={icon} size={15} />
          </div>
        )}
        <input id={autoId} type={type} {...props}
               placeholder={floating ? ' ' : props.placeholder}
               style={{ paddingLeft: icon ? 38 : 14,
                        borderColor: error ? 'var(--red)' : undefined,
                        ...(props.style || {}) }} />
        {floating && label && <label htmlFor={autoId}>{label}</label>}
      </div>
      {error && (
        <span style={{ fontSize:11, color:'var(--red)', display:'flex', alignItems:'center', gap:4 }}>
          <Icon name="alert" size={11} color="var(--red)" /> {error}
        </span>
      )}
    </div>
  );
}

/* ── Toggle — Accessible switch ─────────────────────────────────────────── */
export function Toggle({ checked, onChange, label, disabled = false }) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!disabled) onChange(!checked); }
  };
  return (
    <label style={{ display:'flex', alignItems:'center', gap:10, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <div
        role="switch"
        aria-checked={checked}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && onChange(!checked)}
        onKeyDown={handleKeyDown}
        style={{
          width:44, height:24, borderRadius:12,
          background: checked ? 'var(--cyan)' : 'var(--border)',
          boxShadow: checked ? '0 0 12px rgba(0,207,200,0.3)' : 'none',
          position:'relative', transition:'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
          flexShrink: 0, outline:'none',
        }}
        onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 3px var(--cyan-glow)'; }}
        onBlur={e => { e.currentTarget.style.boxShadow = checked ? '0 0 12px rgba(0,207,200,0.3)' : 'none'; }}
      >
        <div style={{
          position:'absolute', top:2, left: checked ? 22 : 2,
          width:20, height:20, borderRadius:'50%', background:'#fff',
          transition:'left 200ms cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow:'0 1px 4px rgba(0,0,0,0.3)',
        }} />
      </div>
      {label && <span style={{ fontSize:13, color:'var(--text)' }}>{label}</span>}
    </label>
  );
}

/* ── Avatar — With status ring ──────────────────────────────────────────── */
export function Avatar({ name = '', color = 'var(--cyan)', size = 40, url, status }) {
  const initial = name.trim().charAt(0).toUpperCase();
  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
      {url ? (
        <img src={url} alt={name} style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover' }} />
      ) : (
        <div style={{
          width:size, height:size, borderRadius:'50%',
          background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`,
          border: `1px solid ${color}40`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize: size * 0.4, fontWeight:700, color, fontFamily:'var(--font-display)',
        }}>
          {initial}
        </div>
      )}
      {status && (
        <div style={{
          position:'absolute', bottom:0, right:0, width:size*0.3, height:size*0.3,
          borderRadius:'50%', background: status === 'online' ? 'var(--green)' : 'var(--text-muted)',
          border:'2px solid var(--bg-card)',
        }} />
      )}
    </div>
  );
}

/* ── Tabs — with animated indicator ─────────────────────────────────────── */
export function Tabs({ tabs, active, onChange }) {
  const handleKeyDown = (e, i) => {
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    onChange(next);
    e.currentTarget.parentNode.children[next]?.focus();
  };
  return (
    <div role="tablist" style={{ display:'flex', gap:2, background:'var(--bg-elevated)',
                  borderRadius:'var(--radius-sm)', padding:3,
                  border:'1px solid var(--border)', overflowX:'auto', flexShrink:0 }}>
      {tabs.map((tab, i) => (
        <button key={i} role="tab" aria-selected={i === active} tabIndex={i === active ? 0 : -1}
          onClick={() => onChange(i)} onKeyDown={e => handleKeyDown(e, i)}
          style={{
            padding:'7px 16px', borderRadius:6,
            fontSize:13, fontWeight: i === active ? 700 : 400,
            background: i === active ? 'var(--bg-card)' : 'transparent',
            color: i === active ? 'var(--cyan)' : 'var(--text-secondary)',
            border: i === active ? '1px solid var(--border-active)' : '1px solid transparent',
            boxShadow: i === active ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
            whiteSpace:'nowrap', transition:'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
            fontFamily:'var(--font-display)',
          }}>
          {tab}
        </button>
      ))}
    </div>
  );
}

/* ── Modal — Premium with accent line and smooth animation ──────────────── */
export function Modal({ open, onClose, title, children, width = 560, accent }) {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const titleId = useRef('modal-title-' + Math.random().toString(36).slice(2, 8)).current;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus trap + Escape key
  useEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    if (!el) return;
    const focusable = () => el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    // Focus first focusable element
    const nodes = focusable();
    if (nodes.length) nodes[0].focus();

    const handleKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const f = focusable();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  if (!open) return null;
  return (
    <div ref={overlayRef} className="modal-overlay" style={{
      position:'fixed', inset:0, zIndex:1000,
      background:'rgba(4,9,15,0.88)', backdropFilter:'blur(8px)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div ref={contentRef} className="modal-content"
        role="dialog" aria-modal="true" aria-labelledby={titleId}
        style={{
          background:'linear-gradient(180deg, var(--bg-card) 0%, var(--bg-elevated) 100%)',
          border:'1px solid var(--border)',
          borderRadius:'var(--radius-lg)', width:'100%', maxWidth:width,
          maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column',
          boxShadow:'var(--shadow-elevated)',
          position:'relative',
        }}>
        {/* Accent top line */}
        <div style={{
          position:'absolute', top:0, left:0, right:0, height:2,
          background: accent
            ? `linear-gradient(90deg, transparent, ${accent}, transparent)`
            : 'linear-gradient(90deg, transparent, var(--cyan), transparent)',
          opacity: 0.6,
        }} />
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'20px 24px 16px', borderBottom:'1px solid var(--border)' }}>
          <span id={titleId} style={{ fontSize:16, fontWeight:700, fontFamily:'var(--font-display)',
                         letterSpacing:'-0.01em' }}>{title}</span>
          <button onClick={onClose} aria-label="Close" style={{
            background:'var(--bg-elevated)', border:'1px solid var(--border)',
            borderRadius:8, width:32, height:32, display:'flex', alignItems:'center',
            justifyContent:'center', color:'var(--text-secondary)',
            transition:'all 150ms ease',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-active)'; e.currentTarget.style.color = 'var(--text)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        {/* Body */}
        <div style={{ overflowY:'auto', flex:1, padding:'20px 24px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

/* ── AnimatedNumber — Counts up from 0 ──────────────────────────────────── */
export function AnimatedNumber({ value, duration = 600 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const n = typeof value === 'number' ? value : parseInt(value, 10) || 0;
    if (n === 0) { setDisplay(0); return; }
    let start = null;
    const animate = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setDisplay(Math.round(eased * n));
      if (p < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => ref.current && cancelAnimationFrame(ref.current);
  }, [value, duration]);

  return <>{display}</>;
}

/* ── StatCard — Premium with gradient icon bg and animated count ─────────── */
export function StatCard({ label, value, icon, color = 'var(--cyan)', onClick, trend }) {
  return (
    <Card onClick={onClick} className="stat-card" glow={color}
          style={{ padding:20, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:34, fontWeight:900, color, fontFamily:'var(--font-display)',
                        lineHeight:1, marginBottom:6, letterSpacing:'-0.02em',
                        animation: 'countUp 0.4s ease both' }}>
            <AnimatedNumber value={value} />
          </div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', fontWeight:500 }}>
            {label}
            {trend && (
              <span style={{ marginLeft:6, fontSize:10, fontWeight:700,
                             color: trend > 0 ? 'var(--green)' : 'var(--red)' }}>
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
        </div>
        <div style={{
          width:44, height:44, borderRadius:12,
          background: `linear-gradient(135deg, ${color}25 0%, ${color}08 100%)`,
          border: `1px solid ${color}20`,
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow: `0 4px 12px ${color}15`,
        }}>
          <Icon name={icon} size={22} color={color} />
        </div>
      </div>
      {onClick && (
        <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${color}15`,
                      display:'flex', alignItems:'center', gap:4,
                      color, fontSize:11, fontWeight:600, letterSpacing:'0.02em' }}>
          Ver detalle <Icon name="chevronRight" size={12} color={color} />
        </div>
      )}
    </Card>
  );
}

/* ── Toast — Better ─────────────────────────────────────────────────────── */
export function Toast({ message, type = 'success', onClose }) {
  const colors = { success:'var(--green)', error:'var(--red)', info:'var(--cyan)', warning:'var(--amber)' };
  const icons = { success:'check', error:'x', info:'info', warning:'alert' };
  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:9999,
      display:'flex', alignItems:'center', gap:12,
      background:'var(--bg-elevated)',
      border:`1px solid ${colors[type]}30`,
      borderRadius:'var(--radius-md)', padding:'14px 18px',
      boxShadow:'var(--shadow-elevated)', maxWidth:380,
      animation:'fadeInUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
    }}>
      <div style={{
        width:28, height:28, borderRadius:8,
        background: colors[type] + '18',
        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
      }}>
        <Icon name={icons[type]} size={16} color={colors[type]} />
      </div>
      <span style={{ fontSize:13, color:'var(--text)', flex:1, fontWeight:500 }}>{message}</span>
      <button onClick={onClose} style={{ color:'var(--text-muted)', background:'none', padding:4 }}>
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

/* ── Pipeline visual — Two phases ──────────────────────────────────────── */
const ESTADOS = ['solicitada','aceptada','documentacion','visita','evaluacion','votacion','decision','adjudicada','notificada','gala_confirmada','premio_otorgado','titularidad_activa'];
const FASE1 = ['solicitada','aceptada','documentacion','visita','evaluacion','votacion','decision','adjudicada'];
const FASE2_ITEMS = [
  { key:'notificada', label:'Notif.', color:'#00CFC8' },
  { key:'invitada_gala', label:'Gala', color:'#FFB300' },
  { key:'premio_entregado', label:'Premio', color:'#00C896' },
  { key:'factura_emitida', label:'Factura', color:'#1B4B8A' },
  { key:'pago_recibido', label:'Pago', color:'#00C896' },
  { key:'Q1', label:'Q1', color:'#00CFC8' },
  { key:'Q2', label:'Q2', color:'#00CFC8' },
  { key:'Q3', label:'Q3', color:'#00CFC8' },
  { key:'Q4', label:'Q4', color:'#00CFC8' },
  { key:'completada', label:'Fin', color:'#9C27B0' },
];

// State hierarchy for Fila 2 — a check can only be green if current state >= that step
const FASE2_ORDER = ['adjudicada','notificada','gala_confirmada','premio_otorgado','titularidad_activa'];

function getFase2Checks(candidatura, titularidad) {
  if (!candidatura) return {};
  const t = titularidad || {};
  const di = t.derechosImagen || {};
  const tri = t.trimestres || {};
  const estado = candidatura.estado;
  const stateIdx = FASE2_ORDER.indexOf(estado);
  // Helper: is current state at or beyond a given step?
  const atOrBeyond = (step) => stateIdx >= FASE2_ORDER.indexOf(step);

  return {
    notificada: atOrBeyond('notificada') && !!candidatura.fechaNotificacion,
    invitada_gala: atOrBeyond('gala_confirmada') && (
      ['gala_confirmada','premio_otorgado','titularidad_activa'].includes(estado) || !!candidatura.fechaGala
    ),
    premio_entregado: atOrBeyond('premio_otorgado') && !!candidatura.fechaPremio,
    factura_emitida: atOrBeyond('titularidad_activa') && !!di.facturaUrl,
    pago_recibido: atOrBeyond('titularidad_activa') && di.estado === 'pagado',
    Q1: atOrBeyond('titularidad_activa') && tri.Q1?.estado === 'aprobado',
    Q2: atOrBeyond('titularidad_activa') && tri.Q2?.estado === 'aprobado',
    Q3: atOrBeyond('titularidad_activa') && tri.Q3?.estado === 'aprobado',
    Q4: atOrBeyond('titularidad_activa') && tri.Q4?.estado === 'aprobado',
    completada: false, // computed below
  };
}

export function PipelineBar({ estado, candidatura, titularidad }) {
  const { t } = useLang();
  const fase1Idx = FASE1.indexOf(estado);
  const isPostAdjudicada = fase1Idx >= 7 || ['notificada','gala_confirmada','premio_otorgado','titularidad_activa'].includes(estado);
  const fase1Pct = isPostAdjudicada ? 100 : (fase1Idx < 0 ? 0 : ((fase1Idx + 1) / FASE1.length) * 100);
  const color = ESTADO_COLORS[estado] || 'var(--cyan)';

  // Fase 2 checks
  const checks = getFase2Checks(candidatura, titularidad);
  const allDone = checks.notificada && checks.invitada_gala && checks.premio_entregado &&
    checks.factura_emitida && checks.pago_recibido && checks.Q1 && checks.Q2 && checks.Q3 && checks.Q4;
  checks.completada = allDone;

  return (
    <div>
      {/* Fila 1 — Candidatura */}
      <div style={{ marginBottom: isPostAdjudicada ? 14 : 0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
          <span style={{ fontSize:10, fontWeight:600, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
            {t('fase_candidatura') || 'Candidatura'}
          </span>
          <EstadoBadge estado={estado} />
        </div>
        <div style={{ display:'flex', gap:3 }}>
          {FASE1.map((e, i) => {
            const active = i <= (isPostAdjudicada ? 7 : fase1Idx);
            const c = active ? (ESTADO_COLORS[e] || 'var(--cyan)') : 'var(--border)';
            return (
              <div key={e} style={{ flex:1, height:5, borderRadius:3, background: active ? c : 'var(--bg-elevated)',
                                     border: active ? 'none' : '1px solid var(--border)',
                                     transition:'background .4s' }}
                   title={getEstadoLabel(e, t)} />
            );
          })}
        </div>
      </div>

      {/* Fila 2 — Seguimiento (only post-adjudicada) */}
      {isPostAdjudicada && (
        <div>
          <div style={{ fontSize:10, fontWeight:600, color:'var(--text-secondary)', textTransform:'uppercase',
                        letterSpacing:'0.05em', marginBottom:4 }}>
            {t('fase_seguimiento') || 'Seguimiento'}
          </div>
          <div style={{ display:'flex', gap:2, overflowX:'auto', paddingBottom:4 }}>
            {FASE2_ITEMS.map(item => {
              const done = checks[item.key];
              return (
                <div key={item.key} style={{ display:'flex', flexDirection:'column', alignItems:'center',
                                              minWidth:38, gap:3 }}>
                  <div style={{ width:20, height:20, borderRadius:10, display:'flex', alignItems:'center',
                                justifyContent:'center', fontSize:9, fontWeight:800,
                                background: done ? item.color : 'transparent',
                                border: done ? 'none' : `2px solid var(--border)`,
                                color: done ? '#050e1a' : 'var(--text-secondary)',
                                transition:'all .3s' }}>
                    {done ? '\u2713' : ''}
                  </div>
                  <span style={{ fontSize:8, color: done ? item.color : 'var(--text-muted)',
                                  fontWeight: done ? 700 : 400, whiteSpace:'nowrap' }}>
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── ProgressRing — Circular progress indicator ─────────────────────────── */
export function ProgressRing({ value = 0, max = 100, size = 64, strokeWidth = 4,
                                 color = 'var(--cyan)', label }) {
  const radius = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * radius;
  const pct = Math.min(value / max, 1);
  const offset = circ * (1 - pct);
  return (
    <div style={{ position:'relative', width:size, height:size }}>
      <svg width={size} height={size} style={{ transform:'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none"
                stroke="var(--border)" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none"
                stroke={color} strokeWidth={strokeWidth}
                strokeDasharray={circ} strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition:'stroke-dashoffset 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }} />
      </svg>
      <div style={{
        position:'absolute', inset:0, display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
      }}>
        <span style={{ fontSize: size * 0.25, fontWeight:900, color,
                       fontFamily:'var(--font-display)', lineHeight:1 }}>
          {Math.round(pct * 100)}%
        </span>
        {label && <span style={{ fontSize: Math.max(size * 0.12, 8), color:'var(--text-muted)',
                                  marginTop:1 }}>{label}</span>}
      </div>
    </div>
  );
}

/* ── Divider ────────────────────────────────────────────────────────────── */
export function Divider({ label, color = 'var(--border)' }) {
  if (!label) return <div style={{ height:1, background:color, margin:'16px 0' }} />;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, margin:'16px 0' }}>
      <div style={{ flex:1, height:1, background:color }} />
      <span style={{ fontSize:10, fontWeight:600, color:'var(--text-muted)',
                     textTransform:'uppercase', letterSpacing:'0.08em',
                     fontFamily:'var(--font-display)' }}>{label}</span>
      <div style={{ flex:1, height:1, background:color }} />
    </div>
  );
}

/* ── DataTable — Premium with zebra stripes, sortable, hover ─────────────── */
export function DataTable({ columns, data, onRowClick, emptyIcon, emptyTitle, emptySubtitle }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (colKey) => {
    if (sortCol === colKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(colKey); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    if (!sortCol) return data;
    const col = columns.find(c => c.key === sortCol);
    if (!col) return data;
    return [...data].sort((a, b) => {
      const va = col.sortValue ? col.sortValue(a) : (a[sortCol] ?? '');
      const vb = col.sortValue ? col.sortValue(b) : (b[sortCol] ?? '');
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortCol, sortDir, columns]);

  if (data.length === 0 && emptyTitle) {
    return <EmptyState icon={emptyIcon || 'layers'} title={emptyTitle} subtitle={emptySubtitle} />;
  }

  return (
    <div style={{ borderRadius:'var(--radius-md)', border:'1px solid var(--border-card)',
                  overflow:'hidden', background:'var(--bg-card)' }}>
      <table className="data-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key}
                  onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
                  style={{ cursor: col.sortable !== false ? 'pointer' : 'default',
                           width: col.width, userSelect:'none' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  {col.label}
                  {sortCol === col.key && (
                    <span style={{ fontSize:8, opacity:0.7 }}>
                      {sortDir === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={row.id || i}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'clickable-row' : ''}>
              {columns.map(col => (
                <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Skeleton — Loading placeholders ─────────────────────────────────────── */
export function Skeleton({ width = '100%', height = 16, radius = 6, style = {} }) {
  return (
    <div className="skeleton" style={{
      width, height, borderRadius: radius,
      ...style,
    }} />
  );
}
export function SkeletonCard() {
  return (
    <Card style={{ padding:20 }}>
      <Skeleton width="60%" height={14} style={{ marginBottom:10 }} />
      <Skeleton width="40%" height={12} style={{ marginBottom:14 }} />
      <Skeleton width="100%" height={8} />
    </Card>
  );
}

/* ── InfoStrip — Inline info/warning banner ──────────────────────────────── */
export function InfoStrip({ children, color = 'var(--cyan)', icon = 'info' }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      padding:'10px 14px', borderRadius:'var(--radius-sm)',
      background: color + '0a', border: `1px solid ${color}20`,
      fontSize:12, color, lineHeight:1.5,
    }}>
      <div style={{ width:28, height:28, borderRadius:8, flexShrink:0,
                    background: color + '15', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Icon name={icon} size={14} color={color} />
      </div>
      <div style={{ flex:1 }}>{children}</div>
    </div>
  );
}

/* ── ConfirmModal — Replaces window.confirm/alert with styled dark modal ── */
export function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmLabel, cancelLabel, variant = 'danger' }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onCancel} title={title || ''} width={420} accent={variant === 'danger' ? 'var(--red)' : 'var(--amber)'}>
      <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, marginBottom:20 }}>
        {message}
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
        <Btn variant="outline" size="sm" onClick={onCancel}>
          {cancelLabel || 'Cancelar'}
        </Btn>
        <Btn variant={variant === 'danger' ? 'danger' : 'primary'} size="sm" onClick={onConfirm}>
          {confirmLabel || 'Confirmar'}
        </Btn>
      </div>
    </Modal>
  );
}

export { ESTADO_COLORS, ESTADOS };
